import { JsonObject } from 'src/models/model'
import Concern from 'src/models/model/concerns/Concern'

export const TYPES = {
  /** QR_CODE applies to orders made via the website with delivery parameters applied via the router */
  QR_CODE: 'qr-code',
  /** MOBILE_APP applies to orders made via the native mobile app, without a QR_CODE (IOS/Android flags present) */
  MOBILE_APP: 'mobile-app',
  /** WEBSITE applies to orders made via the website, without a QR_CODE or MOBILE_APP */
  WEBSITE: 'website',
  /** PHONE applies to orders made via a register, with the "phone" flag toggled on the Order */
  PHONE: 'phone',
  /** HANDHELD applies to orders made via a register in handheld mode (small screen size) */
  HANDHELD: 'handheld',
  /** REGISTER applies to orders made via a register, without PHONE or HANDHELD */
  REGISTER: 'register',
  /** KIOSK applies to orders made via a kiosk */
  KIOSK: 'kiosk',
  /** EXTERNAL applies to orders made by an external ticketing client, such as a third party ticketer using the API */
  EXTERNAL: 'external'
}
TYPES.ALL = _.values(TYPES)
/** PRIVATE applies to Sales Channels that are accessed by staff, not by customers directly */
TYPES.PRIVATE = [TYPES.PHONE, TYPES.HANDHELD, TYPES.REGISTER]
/** PUBLIC applies to Sales Channels that are accessed by customers directly */
TYPES.PUBLIC = _.without(TYPES.ALL, ...TYPES.PRIVATE)

TYPES.MENU = [TYPES.QR_CODE, TYPES.MOBILE_APP, TYPES.WEBSITE, TYPES.HANDHELD, TYPES.REGISTER, TYPES.KIOSK]
TYPES.TICKETING = [TYPES.WEBSITE, TYPES.REGISTER, TYPES.KIOSK]
TYPES.BOOKING_FEES = [TYPES.WEBSITE, TYPES.PHONE, TYPES.REGISTER, TYPES.KIOSK, TYPES.EXTERNAL]
Object.freeze(TYPES)

export const MENU_DEFAULT = _(TYPES.MENU).keyBy().mapValues((_value, key) => _.includes(TYPES.PRIVATE, key)).value()
export const TICKETING_DEFAULT = _(TYPES.TICKETING).keyBy().mapValues((_value, _key) => true).value()
export const BOOKING_FEES_DEFAULT = _(TYPES.BOOKING_FEES).keyBy().mapValues((_value, key) => _.includes(TYPES.PUBLIC, key)).value()

export default class SalesChannelRestrictable extends Concern {
  static salesChannelRestrictable = true

  get salesChannelRestrictable () {
    return true
  }

  /**
   * @description Override this method on concerned Models to ensure the correct default is used for the channels field.
   *
   * Valid channel keys are defined by SalesChannelRestrictable TYPES.
   * Preset default objects are defined as SalesChannelRestrictable X_DEFAULT.
   *
   * @example
   * get salesChannelRestrictableChannelsDefault () {
   *   return MENU_DEFAULT
   * }
   *
   * @returns {Object} A key-value collection of SalesChannelRestrictable channels and their default values
   */
  get salesChannelRestrictableChannelsDefault () {
    console.error(`${this.constructor.name} must implement salesChannelRestrictableChannelsDefault`)
    return {}
  }

  /**
   * @description Override this method on concerned Models when the Model requires one of many other Models also be available.
   * E.g. A Category is only available if one of it's Items are available. An Item is a selectable option within the Category.
   * Multiple Model collections can be concatenated to return a single array of options, if necessary.
   *
   * @example
   * get salesChannelRestrictableOptions () {
   *   return _.concat(this.items.data, this.itemPackages.data)
   * }
   *
   * @returns {Array} An array of SalesChannelRestrictable models that are selectable options within the current Model.
   */
  get salesChannelRestrictableOptions () {
    return []
  }

  /**
   * @description Override this method on concerned Models to determine if the Sales Channel is inherited from the parent Model.
   * When true, isSalesChannelEnabled will only validate the Model isActive, assuming the inheritee is managing this Model's Sales Channel rules.
   *
   * @returns {Boolean} Whether the Sales Channel is inherited from a parent Model
   */
  get isSalesChannelInherited () {
    return false
  }

  static fields = {
    salesChannels: {
      type: JsonObject,
      mergeDefault: true,
      default () {
        return _.clone(this.salesChannelRestrictableChannelsDefault)
      }
    }
  }

  static computed = {
    availableOnline () {
      // Permits functions such as "featured item"
      return (
        this.isSalesChannelEnabled(TYPES.WEBSITE)
      )
    },
    enabledSalesChannels () {
      return this.$mh.objectToArray(this.salesChannels)
    }
  }

  isSalesChannelEnabled (channel) {
    // If the Sales Channel is inherited, return early
    if (this.isSalesChannelInherited) return true

    if (_.isNil(this.salesChannels)) return false
    if (!this.salesChannels[channel]) return false

    const isOptionAvailable = _.isEmpty(this.salesChannelRestrictableOptions) || _.some(this.salesChannelRestrictableOptions, option => option.isSalesChannelEnabled(channel))

    return isOptionAvailable
  }
}
